import { Builder, BuilderBlocks, type BuilderElement } from '@builder.io/react';
import React from 'react';
import { twMerge } from 'tailwind-merge';

import {
  Button,
  type ButtonProps,
} from '../../storyblocks-design-system/components/atoms/Button/Button';
import { Text } from '../../storyblocks-design-system/components/atoms/Text/Text';

const BUTTON_VARIANTS = [
  'primary',
  'secondary',
  'outlined-dark',
  'outlined-light',
  'text',
  'alternate',
];

export interface FooterHookProps {
  headline: string;
  content: string;
  showCallToAction: boolean;
  callToAction: ButtonProps & { text: string };
  shadowVariant: boolean;
  backgroundColor?: string;
  shadowOptions?: {
    shadowColor: string;
    shadowImage: string;
  };
  dynamicContent: {
    blocks: React.ReactNode[];
  };
  builderBlock: BuilderElement;
}

const FooterHook = ({
  headline,
  showCallToAction,
  callToAction,
  shadowVariant,
  shadowOptions,
  backgroundColor,
  dynamicContent = {
    blocks: [],
  },
  builderBlock,
}: FooterHookProps) => {
  const { shadowColor, shadowImage } = shadowOptions || {};
  const contentClass = twMerge(
    'relative',
    shadowVariant && 'w-[calc(100%-2rem)] left-6'
  );
  const { blocks } = dynamicContent;
  return (
    <div className="px-6 py-16 md:px-12 md:py-24 lg:p-24">
      <div className={contentClass}>
        {shadowVariant ? (
          <div className="absolute -inset-0">
            <div
              className="absolute w-full h-full -left-6 top-6 rounded-3xl"
              style={{
                backgroundColor: shadowColor,
                backgroundImage: shadowImage
                  ? `url(${shadowImage})`
                  : undefined,
                backgroundSize: shadowImage && 'cover',
              }}
              role="presentation"
            />
          </div>
        ) : null}
        <div
          className="relative px-6 py-12 md:p-12 md:gap-4 lg:p-24 rounded-3xl"
          style={{ backgroundColor }}
        >
          <div className="flex flex-col gap-4 lg:flex-row lg:gap-8">
            <Text
              fontFamily="polysans"
              fontSize="4xl"
              fontWeight="semibold"
              className="m-0 flex-1"
              asChild
            >
              <h2>{headline}</h2>
            </Text>
            <div className="flex-1">
              <BuilderBlocks
                parentElementId={builderBlock.id}
                dataPath={`dynamicContent.blocks`}
                blocks={blocks}
              />
              {showCallToAction ? (
                <Button
                  variant={callToAction.variant}
                  className="mt-8"
                  size="large"
                  href={callToAction.href}
                  target={callToAction.target}
                >
                  {callToAction.text}
                </Button>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

Builder.registerComponent(FooterHook, {
  name: 'Footer Hook SB',
  inputs: [
    {
      name: 'headline',
      type: 'string',
      required: true,
      defaultValue: 'Imagine your team with Storyblocks for Business',
    },
    {
      name: 'backgroundColor',
      type: 'color',
      defaultValue: 'var(--color-blue-200, #bbdeff)',
    },
    {
      name: 'shadowVariant',
      type: 'boolean',
      onChange: (options) => {
        if (options.get('shadowVariant')) {
          options.set('backgroundColor', 'var(--color-forest-200, #a7e2d2)');
        } else {
          options.set('backgroundColor', 'var(--color-blue-200, #bbdeff)');
        }
      },
    },
    {
      name: 'shadowOptions',
      type: 'object',
      showIf: (options) => options.get('shadowVariant'),
      defaultValue: {
        shadowColor: 'var(--color-gray-100, #f5f5f5)',
        shadowImage: '',
      },
      subFields: [
        {
          name: 'shadowColor',
          type: 'color',
        },
        {
          name: 'shadowImage',
          type: 'file',
          allowedFileTypes: ['jpg', 'jpeg', 'png', 'webp'],
        },
      ],
    },
    {
      name: 'showCallToAction',
      type: 'boolean',
      defaultValue: true,
    },
    {
      name: 'callToAction',
      type: 'object',
      defaultValue: {
        text: 'Explore Footage',
        href: '/',
        variant: BUTTON_VARIANTS[5],
        size: 'default',
        target: '_self',
        rel: '',
      },
      showIf: (options) => options.get('showCallToAction'),
      subFields: [
        {
          name: 'text',
          type: 'string',
          defaultValue: '',
          required: true,
        },
        {
          name: 'href',
          type: 'string',
          defaultValue: '/',
          required: true,
        },
        {
          name: 'target',
          type: 'string',
          enum: ['_self', '_blank', '_parent', '_top'],
          helperText:
            'For explanation of options, see https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a#attr-target',
        },
        {
          name: 'variant',
          type: 'string',
          defaultValue: BUTTON_VARIANTS[5],
          enum: BUTTON_VARIANTS,
        },
      ],
    },
    {
      name: 'dynamicContent',
      type: 'uiBlocks',
      defaultValue: {
        blocks: [],
      },
    },
  ],
});
